function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import React, { useImperativeHandle, useMemo, useState, forwardRef, useEffect } from 'react';
import { Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import Joyride from 'react-joyride';
import { useTheme } from '@mui/styles';
import { TourHeader, TourBody, TourTooltip, TourBeacon } from "./components";
import { TourContextProvider } from "./context";
var StyledPaper = styled(Paper)(function (_ref) {
  var theme = _ref.theme;
  return {
    position: 'fixed',
    bottom: '24px',
    left: '24px',
    background: theme.palette.tour.background,
    zIndex: 9999,
    '&': {
      borderRadius: '12px 12px 12px 0'
    }
  };
});

/**
 * Creates a UI element containing multiple Feature tours. These tours can contain steps that the
 * user can walk through.
 * @param {object} params
 * @param {TourFeature[]} params.tourFeatures Array of `TourFeature` that `Tour` element will contain.
 * `TourFeature` will be rendered as `TourFeature` elements.
 * @param onTourStepCompleted
 * @return {JSX.Element}
 * @constructor
 */
export var Tour = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var isLoading = _ref2.isLoading,
    defaultOpenFeature = _ref2.defaultOpenFeature,
    _ref2$defaultOpen = _ref2.defaultOpen,
    defaultOpen = _ref2$defaultOpen === void 0 ? false : _ref2$defaultOpen,
    _ref2$tourFeatures = _ref2.tourFeatures,
    tourFeatures = _ref2$tourFeatures === void 0 ? [] : _ref2$tourFeatures,
    _onTourStart = _ref2.onTourStart,
    _onTourExit = _ref2.onTourExit,
    onIndexChange = _ref2.onIndexChange,
    onTourErrorReset = _ref2.onTourErrorReset,
    _onTourToggle = _ref2.onTourToggle,
    _onTourStepToggle = _ref2.onTourStepToggle,
    onTourTooltipClickNext = _ref2.onTourTooltipClickNext,
    onTourTooltipClickBack = _ref2.onTourTooltipClickBack,
    onTourTooltipClickExit = _ref2.onTourTooltipClickExit,
    onTourTooltipClickDone = _ref2.onTourTooltipClickDone,
    _ref2$onTourStepCompl = _ref2.onTourStepCompleted,
    onTourStepCompleted = _ref2$onTourStepCompl === void 0 ? function (_ref3) {
      var id = _ref3.id;
      return id;
    } : _ref2$onTourStepCompl;
  var theme = useTheme();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isTourExpanded = _useState2[0],
    setIsTourExpanded = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isModalOpen = _useState4[0],
    setIsModalOpen = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isErrorMessage = _useState6[0],
    setIsErrorMessage = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    currentActiveTourStep = _useState8[0],
    setCurrentActiveTourStep = _useState8[1];
  var _useState9 = useState(0),
    _useState10 = _slicedToArray(_useState9, 2),
    currentStepIndex = _useState10[0],
    setCurrentStepIndex = _useState10[1];
  var _useState11 = useState(0),
    _useState12 = _slicedToArray(_useState11, 2),
    currentErrorIndex = _useState12[0],
    setCurrentErrorIndex = _useState12[1];
  var _useState13 = useState(null),
    _useState14 = _slicedToArray(_useState13, 2),
    currentOpenFeature = _useState14[0],
    setCurrentOpenFeature = _useState14[1];
  var state = useMemo(function () {
    return {
      isTourExpanded: isTourExpanded,
      isErrorMessage: isErrorMessage,
      setIsTourExpanded: setIsTourExpanded,
      currentActiveTourStep: currentActiveTourStep,
      currentStepIndex: currentStepIndex,
      setCurrentActiveTourStep: setCurrentActiveTourStep,
      currentOpenFeature: currentOpenFeature,
      setCurrentOpenFeature: setCurrentOpenFeature,
      incrementStep: incrementStep,
      decrementStep: decrementStep,
      startTour: startTour,
      exitTour: exitTour,
      clearCurrentTour: clearCurrentTour,
      completeTour: completeTour,
      resetFromError: resetFromError,
      onTourTooltipClickNext: onTourTooltipClickNext,
      onTourTooltipClickBack: onTourTooltipClickBack,
      onTourTooltipClickExit: onTourTooltipClickExit,
      onTourTooltipClickDone: onTourTooltipClickDone
    };
  }, [isTourExpanded, isErrorMessage, currentActiveTourStep, currentStepIndex, currentOpenFeature]);
  var completedStepsCount = useMemo(function () {
    return tourFeatures.filter(function (s) {
      return s.completed;
    }).length;
  }, [tourFeatures]);
  function startTour(tourStep) {
    setCurrentActiveTourStep(tourStep);
    setIsTourExpanded(false);
  }
  function exitTour() {
    var shouldOpen = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    clearCurrentTour();
    setIsTourExpanded(shouldOpen);
  }
  function completeTour() {
    clearCurrentTour();
    expandNextFeature();
    if (onTourStepCompleted) {
      onTourStepCompleted(currentActiveTourStep);
    }
  }
  function expandNextFeature() {
    var currentOpenFeatureIndex = tourFeatures.findIndex(function (t) {
      return t.id === currentActiveTourStep.id;
    });
    if (currentOpenFeatureIndex < tourFeatures.length - 1) {
      setCurrentOpenFeature(tourFeatures[currentOpenFeatureIndex + 1].id);
    } else {
      setCurrentOpenFeature(null);
    }
    setTimeout(function () {
      return setIsTourExpanded(true);
    }, 700);
  }
  function incrementStep() {
    if (!currentActiveTourStep) return;
    if (currentStepIndex >= currentActiveTourStep.steps.length - 1) {
      completeTour();
      return;
    }
    setCurrentStepIndex(currentStepIndex + 1);
  }
  function decrementStep() {
    if (!currentActiveTourStep) return;
    if (currentStepIndex <= 0) return;
    setCurrentStepIndex(currentStepIndex - 1);
  }
  function clearCurrentTour() {
    setCurrentActiveTourStep(null);
    setCurrentStepIndex(0);
    setIsErrorMessage(false);
  }

  /**
   * Forces `Tour` to display a specified step from the current `TourStep.error` array.
   * Accepts an object with property, `index: number` and a callback `filter: (any) => number`.
   * `filter` should return a number that corresponds with step that is contained within the current
   * `TourStep.errors` array.
   * If both `index` and `filter` are specified, `filter` will be ignored.
   * @param {number | null} index
   * @param { ([]) => number } filter
   */
  function displayError(_ref4) {
    var _currentActiveTourSte;
    var _ref4$index = _ref4.index,
      index = _ref4$index === void 0 ? null : _ref4$index,
      _ref4$filter = _ref4.filter,
      filter = _ref4$filter === void 0 ? null : _ref4$filter;
    if (index === null && filter === null) return;
    if (!(currentActiveTourStep !== null && currentActiveTourStep !== void 0 && (_currentActiveTourSte = currentActiveTourStep.errors) !== null && _currentActiveTourSte !== void 0 && _currentActiveTourSte.length)) return;
    var errIndex = index !== null && index !== void 0 ? index : filter(currentActiveTourStep === null || currentActiveTourStep === void 0 ? void 0 : currentActiveTourStep.errors);
    if (errIndex === null || errIndex < 0) return;
    setIsErrorMessage(true);
    setCurrentErrorIndex(errIndex);
  }

  /**
   * Resets tour back to displaying the step what was shown prior to the current error message. If
   * specified, will invoke the `onTourReset` callback.
   */
  function resetFromError() {
    if (!currentActiveTourStep) return;
    setIsErrorMessage(false);
    setCurrentErrorIndex(0);
    if (onTourErrorReset) {
      onTourErrorReset(currentErrorIndex);
    }
  }

  /**
   * Exposes status values and control methods on this component's reference.
   * - `currentActiveTourStep`: the currently active "guide_step" that is managed in this component's state.
   * - `isTourExpanded`: whether the `Tour` widget is open or not.
   * - `currentStepIndex`: the currently active index of the `currentActiveTourStep.steps` sub-step.
   * - `incrementStep()`: increment's the `currentStepIndex` by a value of 1.
   * - `exitTour()`: sets this component's state back to its default values.
   * - `displayError({ index: number | null, filter: ([]) => number })`: forces this component to display a step
   * - `toggleTourWidget()`: toggles expanded/collapsed state of `TourBody`.
   *  present in the `currentActiveTourStep.errors` list. Can specify either an index or a filter callback.
   *  If both are specified, it will default to use the index. Filter should be a function that accepts the
   *  `currentActiveTourSteps.errors` list and returns a number corresponding to an index in said list.
   */
  useImperativeHandle(ref, function () {
    return {
      currentActiveTourStep: currentActiveTourStep,
      isTourExpanded: isTourExpanded,
      currentStepIndex: currentStepIndex,
      incrementStep: incrementStep,
      exitTour: exitTour,
      displayError: displayError,
      resetFromError: resetFromError,
      toggleTourWidget: function toggleTourWidget() {
        return setIsTourExpanded(!isTourExpanded);
      }
    };
  }, [currentActiveTourStep, isTourExpanded, currentStepIndex]);

  /**
   * Invokes `onIndexChange` event handler as side effect of `currentStepIndex` or `currentActiveTourStep`
   * change. Will pass current active `Step` or `Error` to `onIndexChange` event handler.
   */
  useEffect(function () {
    if (currentActiveTourStep && onIndexChange) {
      onIndexChange(currentActiveTourStep[isErrorMessage ? 'errors' : 'steps'][currentStepIndex]);
    }
  }, [currentStepIndex, currentActiveTourStep]);

  /**
   * Assigns `isModalOpen` value of current `Step` or `Error` `isModal`.
   */
  useEffect(function () {
    if (currentActiveTourStep) {
      var _currentActiveTourSte2;
      setIsModalOpen(!!((_currentActiveTourSte2 = currentActiveTourStep[isErrorMessage ? 'errors' : 'steps'][currentStepIndex]) !== null && _currentActiveTourSte2 !== void 0 && _currentActiveTourSte2.isModal));
    }
  }, [currentActiveTourStep, currentStepIndex]);

  /**
   * If `defaultOpen` is specified, `TourBody` will be expanded given `isLoading` is true.
   */
  useEffect(function () {
    if (!defaultOpen) return null;
    if (isLoading) return null;
    var timeout = setTimeout(function () {
      return setIsTourExpanded(true);
    }, 700);
    return function () {
      return clearTimeout(timeout);
    };
  }, [defaultOpen, isLoading]);
  return /*#__PURE__*/React.createElement(TourContextProvider, {
    state: state
  }, /*#__PURE__*/React.createElement(StyledPaper, {
    elevation: 3,
    sx: {
      width: isTourExpanded ? '280px' : '175px',
      transition: 'width 0.25s'
    }
  }, /*#__PURE__*/React.createElement(TourHeader, {
    completedCount: completedStepsCount,
    totalFeatures: tourFeatures.length,
    onTourToggle: function onTourToggle(isExpanded) {
      if (_onTourToggle) {
        _onTourToggle(isExpanded);
      }
    }
  }), /*#__PURE__*/React.createElement(TourBody, {
    isLoading: isLoading,
    tourFeatures: tourFeatures,
    defaultOpenFeature: defaultOpenFeature,
    onTourStart: function onTourStart(step) {
      if (_onTourStart) {
        _onTourStart(step);
      }
    },
    onTourExit: function onTourExit(step) {
      if (_onTourExit) {
        _onTourExit(step);
      }
    },
    onTourStepToggle: function onTourStepToggle(_ref5) {
      var id = _ref5.id,
        isExpanded = _ref5.isExpanded;
      if (_onTourStepToggle) {
        _onTourStepToggle({
          id: id,
          isExpanded: isExpanded
        });
      }
    }
  })), /*#__PURE__*/React.createElement(Joyride, {
    continuous: true,
    disableScrolling: true,
    hideBackButton: true,
    hideCloseButton: true,
    spotlightClicks: true,
    disableOverlay: true,
    stepIndex: isErrorMessage ? currentErrorIndex : currentStepIndex,
    run: currentActiveTourStep !== null,
    steps: currentActiveTourStep === null || currentActiveTourStep === void 0 ? void 0 : currentActiveTourStep[isErrorMessage ? 'errors' : 'steps'],
    floaterProps: {
      disableAnimation: true,
      hideArrow: isModalOpen
    },
    tooltipComponent: TourTooltip,
    beaconComponent: TourBeacon,
    styles: {
      options: {
        zIndex: 600,
        arrowColor: theme.palette.onboardingTour.background
      }
    }
  }));
});
Tour.displayName = 'Tour';